import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Snowflake, Package, Dumbbell, Leaf, WheatOff, ArrowRight, Sunrise } from 'lucide-react';
import { Button } from '@/components/ui/button';

const SANDWICH_IMG = 'https://customer-assets.emergentagent.com/job_vegan-danish-bakery/artifacts/qc47pguj_Vegan_Danish_Breakfast_Grab.png';
const PATTY_IMG = 'https://customer-assets.emergentagent.com/job_vegan-danish-bakery/artifacts/j5n20xru_Vegan_Danish_Egg_Patty.png';

const HIGHLIGHTS = [
  { Icon: Snowflake, label: 'Frozen', body: 'Ships and stores frozen — thaw, heat & serve on your schedule' },
  { Icon: Package, label: 'Individually wrapped', body: 'Case-ready for micro-markets, kiosks and grab-and-go coolers' },
  { Icon: Dumbbell, label: 'High-protein', body: 'Plant-protein builds that keep students and guests full' },
  { Icon: Leaf, label: 'Vegan', body: '100% plant-based — zero dairy, zero eggs' },
  { Icon: WheatOff, label: 'Gluten-free options', body: 'Gluten-free bread builds available across the line' },
];

export const BreakfastProgram = () => {
  return (
    <section id="breakfast-program" className="relative py-24 md:py-28 bg-primary text-primary-foreground overflow-hidden" data-testid="breakfast-program-section">
      <div className="absolute inset-0 bg-noise opacity-[0.05]" />
      <div className="absolute -top-24 right-16 h-72 w-72 rounded-full bg-accent/15 blur-3xl" />

      <div className="container mx-auto relative grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-foreground/10 border border-primary-foreground/15 text-xs font-medium">
            <Sunrise className="h-3.5 w-3.5 text-accent" /> Signature program
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-medium leading-tight mt-5 text-balance">
            Grab-and-Go Breakfast<br/>
            <span className="italic font-light text-accent">Sandwich Program.</span>
          </h2>
          <p className="mt-5 text-primary-foreground/80 text-base md:text-lg max-w-xl">
            A turnkey morning solution for high-volume foodservice: pre-built plant-based breakfast
            sandwiches that go from freezer to warmer to hand — no bakery staff, no assembly line.
          </p>

          <ul className="mt-8 space-y-3">
            {HIGHLIGHTS.map(({ Icon, label, body }) => (
              <li key={label} className="flex items-start gap-3.5">
                <div className="h-9 w-9 shrink-0 rounded-xl bg-primary-foreground/10 border border-primary-foreground/15 grid place-items-center text-accent">
                  <Icon className="h-4 w-4" />
                </div>
                <div>
                  <span className="font-display font-semibold">{label}</span>
                  <span className="text-primary-foreground/70 text-sm"> — {body}</span>
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-9 flex flex-wrap gap-3">
            <Button asChild size="lg" className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90 px-7 h-12" data-testid="breakfast-program-cta">
              <Link to="/products/breakfast-grab-and-go">
                See the breakfast line <ArrowRight className="ml-1.5 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="rounded-full border-primary-foreground/25 bg-transparent text-primary-foreground hover:bg-primary-foreground/10 px-6 h-12">
              <Link to="/products/egg-patties">Plant-based egg patties</Link>
            </Button>
          </div>
        </div>

        <div className="lg:col-span-6 relative">
          <div className="grid grid-cols-12 gap-4 items-center">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="col-span-7 aspect-[4/5] rounded-[2rem] overflow-hidden shadow-pop"
            >
              <img src={SANDWICH_IMG} alt="Individually wrapped plant-based breakfast sandwiches" className="h-full w-full object-cover" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.12 }}
              className="col-span-5 aspect-[4/5] rounded-[2rem] overflow-hidden shadow-pop"
            >
              <img src={PATTY_IMG} alt="Plant-based egg patties and breakfast sandwiches" className="h-full w-full object-cover" />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BreakfastProgram;
